// One-time "AI Prototypes is now Holo" announcement. Shows once per browser
// (localStorage gate) on the first signed-in portal load after the rebrand
// ships. Never rendered on the sign-in screen. Skill users never see it —
// the skill keeps the original name and hardcoded URL by design.

const SEEN_KEY = "holoRebrandNoticeSeen";
const MODAL_ID = "rebrand-notice-modal";

// Pure gate, separated for tests. localStorage access can throw (blocked
// third-party storage, some private modes) — treat unavailable storage as
// "seen" so a broken storage layer can't turn a one-time notice into an
// every-load nag.
export function shouldShowRebrandNotice(storage: Pick<Storage, "getItem">): boolean {
  try {
    return storage.getItem(SEEN_KEY) === null;
  } catch {
    return false;
  }
}

export function markRebrandNoticeSeen(storage: Pick<Storage, "setItem">): void {
  try {
    storage.setItem(SEEN_KEY, "1");
  } catch {
    /* worst case the notice shows again next load */
  }
}

/**
 * Show the rebrand notice if this browser hasn't dismissed it before.
 * Call after the signed-in app has rendered. Any dismissal — "Got it",
 * the × button, Escape, or a backdrop click — marks it seen.
 */
export function maybeShowRebrandNotice(): void {
  if (!shouldShowRebrandNotice(localStorage)) return;
  if (document.getElementById(MODAL_ID)) return;
  // Don't stack on top of the help modal if the user deep-linked to #help;
  // seen isn't marked, so the notice simply waits for the next visit.
  if (location.hash === "#help") return;

  const overlay = document.createElement("div");
  overlay.id = MODAL_ID;
  overlay.className = "modal-overlay";
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-modal", "true");
  overlay.setAttribute("aria-label", "AI Prototypes is now Holo");

  const dialog = document.createElement("div");
  dialog.className = "modal rebrand-notice";
  dialog.addEventListener("click", (e) => e.stopPropagation());
  dialog.innerHTML = `
    <button type="button" class="modal-close" aria-label="Dismiss">&times;</button>
    <img src="/favicon.svg" alt="" class="rebrand-logo" />
    <h2><span class="rebrand-old">AI Prototypes</span> is now <strong>Holo</strong></h2>
    <p>Same portal, new name — because you're sharing a lot more than prototypes
      these days. We have a new home, so update your bookmark:</p>
    <a class="rebrand-url" href="https://holo.ai.rewind.engineering">holo.ai.rewind.engineering</a>
    <p>Old links to <code>prototypes.ai.rewind.engineering</code> keep working,
      so nothing breaks in the meantime.</p>
    <div class="rebrand-unchanged">
      <strong>Your shared links are not affected.</strong> Viewer URLs
      (<code>p.ai.rewind.engineering/…</code>) are exactly the same as before —
      nothing you've sent to anyone changes.
    </div>
    <button type="button" class="btn-primary rebrand-dismiss">Got it</button>
  `;

  function dismiss() {
    markRebrandNoticeSeen(localStorage);
    document.removeEventListener("keydown", onKeydown);
    overlay.remove();
    document.body.classList.remove("modal-open");
  }

  // Escape dismisses; Tab cycles within the dialog (same focus-trap rules as
  // the help modal — tabbing must not reach content behind the overlay).
  function onKeydown(e: KeyboardEvent) {
    if (e.key === "Escape") {
      dismiss();
      return;
    }
    if (e.key !== "Tab") return;
    const focusables = overlay.querySelectorAll<HTMLElement>("a[href], button:not([disabled])");
    if (focusables.length === 0) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && active === last) {
      e.preventDefault();
      first.focus();
    }
  }

  dialog.querySelector(".modal-close")!.addEventListener("click", dismiss);
  dialog.querySelector(".rebrand-dismiss")!.addEventListener("click", dismiss);
  overlay.addEventListener("click", dismiss);
  document.addEventListener("keydown", onKeydown);

  overlay.append(dialog);
  document.body.append(overlay);
  document.body.classList.add("modal-open");

  (dialog.querySelector(".rebrand-dismiss") as HTMLElement).focus();
}
